import { z } from 'zod';
import { UpholsteryDesignKeySchema } from './upholstery-design.schema';

/** One design-type's fabric allowance entry. All fields optional/nullable.
 *  Missing/null fields mean "not set" — caller falls through to next layer.
 *  Explicit 0 means "use 0%" (overrides material default).
 *  `.strict()` rejects unknown keys to catch typos in stored JSON. */
export const UpholsteryDesignAllowanceEntrySchema = z.object({
  flat_pct: z.number().min(0).nullable().optional(),
  per_channel_pct: z.number().min(0).nullable().optional(),
  per_button_pct: z.number().min(0).nullable().optional(),
}).strict();

/**
 * Design key -> allowance entry. A record, not a closed object.
 *
 * It used to list the eight keys explicitly and require every one. That had two
 * failure modes for an admin-created design: the new key was accepted with HTTP
 * 200 and then silently stripped by z.object, and `allowances[newKey]` had no
 * index signature so it could not even be written in TypeScript.
 *
 * A missing key means "not configured" and the caller falls through to the
 * material default — which is already what resolveDesignTypeAllowancePct does
 * for an entry of `{}`, so nothing about the resolution changes.
 */
export const UpholsteryDesignAllowancesSchema = z.record(
  UpholsteryDesignKeySchema,
  UpholsteryDesignAllowanceEntrySchema,
);

export const UpholsteryDesignSettingsSchema = z.object({
  id: z.string().uuid(),
  design_allowance_by_type: UpholsteryDesignAllowancesSchema,
  /**
   * The button hardware used when a template's design does not name its own.
   *
   * Null means no default, which is what every environment had before this
   * existed: the cost helpers fall through and price no button hardware. It is
   * validated as a uuid so a malformed value fails at the API rather than
   * looking configured and silently resolving to nothing.
   */
  default_button_hardware_id: z.string().uuid().nullable(),
  /**
   * The tufting wire hardware used when a template's design does not name its
   * own.
   *
   * Null means no default, which is what every environment has until an
   * administrator picks one — and until then this branch changes no price at
   * all. It is validated as a uuid so a malformed value fails at the API
   * rather than looking configured and silently resolving to nothing.
   *
   * Unlike the button default, an id that resolves to nothing no longer fails
   * quietly: resolveWireHardware emits a "was not found in the Hardware
   * Library" warning for it.
   */
  default_wire_hardware_id: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const UpdateUpholsteryDesignSettingsSchema = UpholsteryDesignSettingsSchema
  .omit({ id: true, created_at: true, updated_at: true })
  .partial();

export type UpholsteryDesignAllowanceEntry = z.infer<typeof UpholsteryDesignAllowanceEntrySchema>;
export type UpholsteryDesignAllowances = z.infer<typeof UpholsteryDesignAllowancesSchema>;
export type UpholsteryDesignSettings = z.infer<typeof UpholsteryDesignSettingsSchema>;
export type UpdateUpholsteryDesignSettings = z.infer<typeof UpdateUpholsteryDesignSettingsSchema>;
